import React, { useRef, useState } from "react";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { COLOR_PALETTE } from "./taxonomyApi";

/**
 * The small filled square next to a tag name. Click it to change the colour.
 *
 * This is the first colour picker in the admin, so it is deliberately narrow:
 * twelve curated swatches (the same list the server assigns from) plus a
 * native <input type="color"> for anything else. No third-party picker —
 * ENGINEERING-NOTES reserves semantic colour for severity, and a full colour
 * wheel invites tags that are unreadable as chips on the public site.
 *
 * Props:
 *   value     current hex, e.g. "#2563eb"
 *   onChange  (hex) => void, fired on selection
 *   size      px, default 18
 *   disabled  renders the swatch but does not open the picker
 *   title     tooltip override
 */
export default function ColorSwatch({
  value,
  onChange,
  size = 18,
  disabled = false,
  title = "Change colour",
}) {
  const [anchor, setAnchor] = useState(null);
  const nativeRef = useRef(null);
  const current = value || COLOR_PALETTE[0];

  const pick = (hex) => {
    onChange?.(hex);
    setAnchor(null);
  };

  return (
    <>
      <button
        type="button"
        aria-label={title}
        title={title}
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        style={{
          width: size,
          height: size,
          minWidth: size,
          padding: 0,
          borderRadius: 4,
          border: "1px solid rgba(0,0,0,0.18)",
          background: current,
          cursor: disabled ? "default" : "pointer",
          // Keeps the square from being squashed inside a flex row of text.
          flex: "0 0 auto",
        }}
      />

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, width: 208 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
            Preset colours
          </Typography>

          <Box
            sx={{
              mt: 1,
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: 0.75,
            }}
          >
            {COLOR_PALETTE.map((hex) => (
              <button
                key={hex}
                type="button"
                title={hex}
                onClick={() => pick(hex)}
                style={{
                  width: 24,
                  height: 24,
                  padding: 0,
                  borderRadius: 4,
                  background: hex,
                  cursor: "pointer",
                  // The current colour gets a ring rather than a tick, so the
                  // swatch colour itself stays readable.
                  border:
                    hex.toLowerCase() === String(current).toLowerCase()
                      ? "2px solid #111"
                      : "1px solid rgba(0,0,0,0.18)",
                }}
              />
            ))}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", flex: 1 }}>
              Custom
            </Typography>
            <input
              ref={nativeRef}
              type="color"
              value={current}
              onChange={(e) => onChange?.(e.target.value)}
              style={{
                width: 34,
                height: 26,
                padding: 0,
                border: "1px solid rgba(0,0,0,0.18)",
                borderRadius: 4,
                background: "none",
                cursor: "pointer",
              }}
            />
          </Box>

          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1, color: "text.secondary", fontFamily: "monospace" }}
          >
            {String(current).toLowerCase()}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
